import { useEffect, useState } from 'react';
import { Post, Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { useFeedGeteway } from '../../../Hooks/UseFeedGateway';
import { useBackend } from '../../../Hooks/UseBackend';
import { useLatestCallbackRef } from '../../../Hooks/useLatestCallbackRef';

import { PostsFeed } from '../types';

export interface UseFeedProps {
    sources: Source[];
    onNewPost: (post: Post) => void;
}

export function useFeed({ sources, onNewPost }: UseFeedProps) {
    const { getFeed } = useBackend();

    const [feed, setFeed] = useState<PostsFeed>({ Everyone: [] });

    const serializedSources = JSON.stringify(sources);

    useEffect(() => {
        let ignore = false;

        if (ignore) return;

        const sources: Source[] = JSON.parse(serializedSources);

        sources.forEach(async (s) => {
            const posts = await getFeed([s]);

            setFeed((prev) => ({
                ...prev,
                [s.username]: posts,
            }));
        });

        (async () => {
            const posts = await getFeed(sources);

            setFeed((prev) => ({
                ...prev,
                Everyone: posts,
            }));
        })();

        return () => {
            ignore = true;
        };
    }, [serializedSources]);

    return useFreshFeeds({ feed, onNewPost });
}

const emptyFeed = { Everyone: [] };

function useFreshFeeds({ feed, onNewPost }: { feed: PostsFeed; onNewPost: (post: Post) => void }): PostsFeed {
    const { feedGatewayClient } = useFeedGeteway();

    const [freshFeed, setFreshFeed] = useState<PostsFeed>(emptyFeed);

    const onNewPostRef = useLatestCallbackRef(onNewPost);

    useEffect(() => {
        if (!feedGatewayClient) return;

        feedGatewayClient.on(Api.Feed.Gateway.Event.NewPost, async (post: Api.Feed.DTO.Post) => {
            try {
                setFreshFeed((prev) => ({
                    ...prev,
                    Everyone: [post, ...prev.Everyone].slice(0, 20),
                    [post.username]: [post, ...Array.from(prev[post.username] ?? [])].slice(0, 20),
                }));

                onNewPostRef.current(post);
            } catch (err) {
                console.error(err);
            }
        });

        feedGatewayClient.on(Api.Feed.Gateway.Event.PostUpdate, async (update: Api.Feed.DTO.Post) => {
            try {
                setFreshFeed((prev) => {
                    const everyonePosts = [...prev.Everyone];
                    const posts = Array.from(prev[update.username] ?? []);

                    const postInEveryoneIndex = everyonePosts.findIndex((p) => p.id === update.id);
                    const postIndex = posts.findIndex((p) => p.id === update.id);

                    everyonePosts[postInEveryoneIndex] = { ...everyonePosts[postInEveryoneIndex], ...update };
                    posts[postIndex] = { ...posts[postIndex], ...update };

                    return {
                        ...prev,
                        Everyone: everyonePosts,
                        [update.username]: posts,
                    };
                });
            } catch (err) {
                console.error(err);
            }
        });

        return () => {
            feedGatewayClient.off(Api.Feed.Gateway.Event.NewPost);
            feedGatewayClient.off(Api.Feed.Gateway.Event.PostUpdate);
        };
    }, [feedGatewayClient, onNewPostRef]);

    return Object.keys(freshFeed).reduce(
        (mergedFeed, key) => ({
            ...mergedFeed,
            [key]: [...(freshFeed[key] ?? []), ...(mergedFeed[key] ?? [])].slice(0, 20),
        }),
        { ...feed },
    );
}
